import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/admin/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/admin/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/admin/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/admin/components/ui/tabs';
import {
  AdminChatAmbiguousIdentityError,
  fetchAdminConversation,
  fetchAdminConversationTrace,
  type AdminChatIdentityCandidate,
  type AdminConversationResponse,
} from '@/admin/features/chat-analytics/api';
import { ConversationMessagesPanel } from '../components/messages/conversation-messages-panel';
import { CopyId } from '../components/trace/copy-id';
import {
  TraceExplorer,
  type TraceExplorerTab,
} from '../components/trace/trace-explorer';
import { formatDateTime } from '../format';

const DETAIL_LIMIT = 500;
const TRACE_LIMIT = 1000;
const PAGE_TABS = ['messages', 'trace', 'performance', 'raw'] as const;
type ConversationPageTab = (typeof PAGE_TABS)[number];

type ConversationFileRefRow = {
  key: string;
  messageID: string;
  refID?: string;
  kind?: string;
  path?: string;
  displayURL?: string;
  authScope?: string;
  legacy: boolean;
};

export function ConversationDetailPage() {
  const { conversationId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const tenantID = searchParams.get('tenant_id') || undefined;
  const agentID = searchParams.get('agent_id') || undefined;
  const agentGroupID = searchParams.get('agent_group_id') || undefined;
  const userID = searchParams.get('user_id') || undefined;
  const sessionID = searchParams.get('session_id') || undefined;
  const sessionRunID = searchParams.get('session_run_id') || undefined;
  const selectedPageTab = PAGE_TABS.includes(
    searchParams.get('tab') as ConversationPageTab
  )
    ? (searchParams.get('tab') as ConversationPageTab)
    : 'messages';
  const [detail, setDetail] = React.useState<AdminConversationResponse | null>(
    null
  );
  const [candidates, setCandidates] = React.useState<
    AdminChatIdentityCandidate[]
  >([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let canceled = false;
    setLoading(true);
    setError(null);
    setCandidates([]);
    fetchAdminConversation(conversationId, {
      tenant_id: tenantID,
      agent_id: agentID,
      agent_group_id: agentGroupID,
      user_id: userID,
      session_id: sessionID,
      session_run_id: sessionRunID,
      limit: DETAIL_LIMIT,
    })
      .then(response => {
        if (!canceled) setDetail(response);
      })
      .catch(err => {
        if (canceled) return;
        setDetail(null);
        if (err instanceof AdminChatAmbiguousIdentityError) {
          setCandidates(err.candidates);
          setError(err.message);
          return;
        }
        setError(
          err instanceof Error ? err.message : 'Failed to load conversation'
        );
      })
      .finally(() => {
        if (!canceled) setLoading(false);
      });
    return () => {
      canceled = true;
    };
  }, [
    agentGroupID,
    agentID,
    conversationId,
    sessionID,
    sessionRunID,
    tenantID,
    userID,
  ]);

  const loadTrace = React.useCallback(
    (requestID?: string) =>
      fetchAdminConversationTrace(conversationId, {
        tenant_id: tenantID,
        agent_id: agentID,
        agent_group_id: agentGroupID,
        user_id: userID,
        session_id: sessionID,
        session_run_id: sessionRunID,
        request_id: requestID,
        limit: TRACE_LIMIT,
      }),
    [
      agentGroupID,
      agentID,
      conversationId,
      sessionID,
      sessionRunID,
      tenantID,
      userID,
    ]
  );

  const identityKey = [
    conversationId,
    tenantID || '',
    agentID || '',
    agentGroupID || '',
    userID || '',
    sessionID || '',
    sessionRunID || '',
  ].join(':');

  const conversation = detail?.conversation;
  const fileRefs = React.useMemo(
    () => (detail ? conversationFileRefRows(detail) : []),
    [detail]
  );
  const selectTraceRequest = React.useCallback(
    (requestID: string) => {
      if (!requestID) return;
      const next = new URLSearchParams(searchParams);
      next.set('tab', 'trace');
      next.set('request_id', requestID);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );
  const setPageTab = React.useCallback(
    (tab: string) => {
      if (!PAGE_TABS.includes(tab as ConversationPageTab)) return;
      const next = new URLSearchParams(searchParams);
      next.set('tab', tab);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );
  const setTraceExplorerTab = React.useCallback(
    (tab: TraceExplorerTab) => setPageTab(tab),
    [setPageTab]
  );
  const selectConversationCandidate = React.useCallback(
    (candidate: AdminChatIdentityCandidate) => {
      const next = new URLSearchParams(searchParams);
      next.set('tenant_id', candidate.tenant_id);
      next.set('agent_id', candidate.agent_id);
      setOrDelete(next, 'agent_group_id', candidate.agent_group_id);
      setOrDelete(next, 'user_id', candidate.user_id);
      setOrDelete(next, 'session_id', candidate.session_id);
      next.delete('session_run_id');
      next.delete('request_id');
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  return (
    <div className="flex flex-1 flex-col gap-4 overflow-auto py-4 lg:py-6">
      <div className="px-4 lg:px-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-normal">
              Conversation
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <CopyId value={conversationId} label="conversation id" />
              {conversation?.session_id ? (
                <CopyId
                  value={conversation.session_id}
                  label="runtime session"
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="px-4 text-sm text-muted-foreground lg:px-6">
          Loading...
        </div>
      ) : null}
      {error ? (
        <div className="px-4 text-sm text-destructive lg:px-6">{error}</div>
      ) : null}

      {candidates.length > 0 ? (
        <Card className="mx-4 lg:mx-6">
          <CardHeader>
            <CardTitle>Matching Candidates</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                  <TableRow>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Agent</TableHead>
                    <TableHead>Group</TableHead>
                    <TableHead>Session</TableHead>
                    <TableHead>Conversation</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                {candidates.map(candidate => (
                  <TableRow
                    key={`${candidate.tenant_id}:${candidate.agent_id}:${candidate.session_id}`}
                  >
                    <TableCell>{candidate.tenant_id}</TableCell>
                    <TableCell>{candidate.agent_id}</TableCell>
                    <TableCell>{candidate.agent_group_id || '-'}</TableCell>
                    <TableCell>
                      <CopyId value={candidate.session_id} label="session id" />
                    </TableCell>
                    <TableCell>{candidate.conversation_id}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        onClick={() => selectConversationCandidate(candidate)}
                      >
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : null}

      {detail ? (
        <>
          <Card className="mx-4 lg:mx-6">
            <CardHeader>
              <CardTitle>Conversation Context</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 text-sm md:grid-cols-2">
              <Field label="Title" value={conversation?.title || 'Untitled'} />
              <Field label="Status" value={conversation?.status} />
              <Field label="Tenant" value={conversation?.tenant_id} />
              <Field label="Agent" value={conversation?.agent_id} />
              <Field label="Agent group" value={conversation?.agent_group_id} />
              <Field label="User" value={conversation?.user_id} />
              <Field
                label="Session filter"
                value={sessionID || 'All related sessions'}
              />
              <Field
                label="Created"
                value={formatDateTime(conversation?.created_at)}
              />
              <Field
                label="Last activity"
                value={formatDateTime(conversation?.last_activity_at)}
              />
              <Field
                label="Messages"
                value={String(conversation?.message_count ?? 0)}
              />
              <Field
                label="File refs"
                value={`${detail.file_refs.v2_refs} v2 / ${detail.file_refs.legacy_refs} legacy`}
              />
            </CardContent>
          </Card>

          <div className="px-4 lg:px-6">
            <Tabs value={selectedPageTab} onValueChange={setPageTab}>
              <TabsList>
                <TabsTrigger value="messages">Messages</TabsTrigger>
                <TabsTrigger value="trace">Trace</TabsTrigger>
                <TabsTrigger value="performance">Performance</TabsTrigger>
                <TabsTrigger value="raw">Raw</TabsTrigger>
              </TabsList>
              <TabsContent value="messages">
                <div className="-mx-4 lg:-mx-6">
                  <ConversationMessagesPanel
                    messages={detail.messages}
                    onRequestSelect={selectTraceRequest}
                  />
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {selectedPageTab !== 'messages' ? (
            <TraceExplorer
              identityKey={identityKey}
              loadTrace={loadTrace}
              candidateTitle="Choose conversation identity"
              activeTab={selectedPageTab}
              onTabChange={setTraceExplorerTab}
              hideTabs
              buildCandidateTarget={candidate => ({
                tenant_id: candidate.tenant_id,
                agent_id: candidate.agent_id,
                agent_group_id: candidate.agent_group_id,
                user_id: candidate.user_id,
                session_id: candidate.session_id,
              })}
            />
          ) : null}

          <Card className="mx-4 lg:mx-6">
            <CardHeader>
              <CardTitle>Files</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Message</TableHead>
                    <TableHead>Kind</TableHead>
                    <TableHead>Ref</TableHead>
                    <TableHead>Delivery</TableHead>
                    <TableHead>Auth</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fileRefs.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="text-sm text-muted-foreground"
                      >
                        No file refs.
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {fileRefs.map(ref => (
                    <TableRow key={ref.key}>
                      <TableCell>
                        <CopyId value={ref.messageID} label="message id" />
                      </TableCell>
                      <TableCell>
                        {ref.kind || (ref.legacy ? 'legacy' : '-')}
                      </TableCell>
                      <TableCell className="max-w-[18rem] truncate">
                        {ref.refID || ref.path || '-'}
                      </TableCell>
                      <TableCell className="max-w-[24rem] truncate">
                        {ref.displayURL || '-'}
                      </TableCell>
                      <TableCell>{ref.authScope || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card className="mx-4 lg:mx-6">
            <CardHeader>
              <CardTitle>Runtime Sessions</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Session</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Messages</TableHead>
                    <TableHead>Last activity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detail.associated_sessions.map(session => (
                    <TableRow key={session.session_id}>
                      <TableCell>
                        <CopyId value={session.session_id} label="session id" />
                      </TableCell>
                      <TableCell>
                        {session.status || session.activity_status}
                      </TableCell>
                      <TableCell>{session.message_count}</TableCell>
                      <TableCell>
                        {formatDateTime(session.last_activity_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-normal text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 break-all">{value || 'Unknown'}</div>
    </div>
  );
}

function conversationFileRefRows(
  detail: AdminConversationResponse
): ConversationFileRefRow[] {
  const rows: ConversationFileRefRow[] = [];
  detail.messages.forEach(message => {
    parseFileRefs(message.file_refs_json).forEach((ref, index) => {
      rows.push({
        key: `${message.message_id}:${index}`,
        messageID: message.message_id,
        refID: stringValue(ref.ref_id) ?? stringValue(ref.refId),
        kind: stringValue(ref.kind),
        path: stringValue(ref.path),
        displayURL:
          stringValue(ref.display_url) ??
          stringValue(ref.displayURL) ??
          stringValue(ref.displayUrl) ??
          stringValue(ref.download_url) ??
          stringValue(ref.downloadURL) ??
          stringValue(ref.downloadUrl),
        authScope:
          stringValue(ref.auth_scope) ??
          stringValue(ref.authScope) ??
          stringValue(ref.source),
        legacy: !stringValue(ref.ref_id) && !stringValue(ref.refId),
      });
    });
  });
  return rows;
}

function parseFileRefs(value?: string | null): Record<string, unknown>[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item): item is Record<string, unknown> =>
        typeof item === 'object' && item !== null
    );
  } catch {
    return [];
  }
}

function stringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function setOrDelete(params: URLSearchParams, key: string, value?: string) {
  if (value) {
    params.set(key, value);
  } else {
    params.delete(key);
  }
}
